import React from 'react';
import PropTypes from 'prop-types';

// Map platform to icon name
const platformToIcon = {
  youtube: 'youtube',
  tiktok: 'tiktok',
  instagram: 'instagram',
  facebook: 'facebookPost',
  twitter: 'twitterPost',
  podcast: 'podcast',
  website: 'website',
};

const getPlatformFromType = (dealContentType) => {
  if (!dealContentType) return null;
  const typeLower = dealContentType.toLowerCase();
  if (typeLower.includes('youtube')) return 'youtube';
  if (typeLower.includes('tiktok')) return 'tiktok';
  if (typeLower.includes('instagram')) return 'instagram';
  if (typeLower.includes('facebook')) return 'facebook';
  if (typeLower.includes('twitter') || typeLower.includes('x')) return 'twitter';
  if (typeLower.includes('podcast')) return 'podcast';  
  return 'website';
};

const getPlatformFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  const urlLower = url.toLowerCase();

  // YouTube
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
    return { platform: 'youtube', type: 'YouTube' };
  }

  // TikTok
  if (urlLower.includes('tiktok.com')) {
    return { platform: 'tiktok', type: 'TikTok' };
  }

  // Instagram
  if (urlLower.includes('instagram.com')) {
    return { platform: 'instagram', type: 'Instagram' };
  }

  // Facebook
  if (urlLower.includes('facebook.com') || urlLower.includes('fb.com')) {
    return { platform: 'facebook', type: 'Facebook' };
  }

  // Twitter/X
  if (urlLower.includes('twitter.com') || urlLower.includes('x.com')) {
    return { platform: 'twitter', type: 'X' };
  }

  // Podcast platforms
  if (urlLower.includes('spotify.com') ||
      urlLower.includes('apple.com/podcast') ||
      urlLower.includes('soundcloud.com') ||
      urlLower.includes('anchor.fm') ||
      urlLower.includes('podcasts.google.com')) {
    return { platform: 'podcast', type: 'Podcast' };
  }

  return { platform: 'website', type: 'Website' };
};

const ChannelTypeLink = ({ deal }) => {
  const { dealContentType, mediaContentUrl, creator } = deal;
  const mediaChannels = creator?.mediaChannels;

  // If no dealContentType, try to detect from mediaContentUrl
  let platform = null;
  let displayType = dealContentType;
  let icon = 'default';

  if (dealContentType) {
    platform = getPlatformFromType(dealContentType);
    displayType = dealContentType;
    // Use platform-based icon mapping
    icon = platform ? platformToIcon[platform] || 'default' : 'default';
  } else if (mediaContentUrl) {
    // No dealContentType, try to detect from URL
    const detected = getPlatformFromUrl(mediaContentUrl);
    if (detected) {
      platform = detected.platform;
      displayType = detected.type;
      icon = platformToIcon[platform] || 'default';
    } else {
      // Could not detect platform from URL
      return <span>—</span>;
    }
  } else {
    // No dealContentType and no URL
    return <span>—</span>;
  }

  // Find matching channel from mediaChannels (ONLY link to channel, not to mediaContentUrl)
  let channelUrl = null;
  if (platform && mediaChannels && Array.isArray(mediaChannels)) {
    const channel = mediaChannels.find(ch => ch.accountType === platform);
    if (channel && channel.url) {
      channelUrl = channel.url;
    }
  }

  const iconElement = (
    <img
      style={{ width: '30px', cursor: channelUrl ? 'pointer' : 'default' }}
      src={`/images/mediacontent/${icon}.png`}
      alt={displayType}
      title={displayType}
    />
  );

  if (channelUrl) {
    return (
      <a href={channelUrl} target="_blank" rel="noopener noreferrer">
        {iconElement}
      </a>
    );
  }

  return <span>{iconElement}</span>;
};

ChannelTypeLink.propTypes = {
  deal: PropTypes.shape({
    dealContentType: PropTypes.string,
    mediaChannels: PropTypes.arrayOf(PropTypes.shape({
      accountType: PropTypes.string,
      url: PropTypes.string,
    })),
    mediaContentUrl: PropTypes.string,
  }).isRequired,
};

export default ChannelTypeLink;
